import { Components } from '../../../styles';
import { buttonTheme } from './button';
import { inputTheme } from './input';
import { labelTheme } from './label';
import { dividerTheme } from './divider';
import { menuItemTheme } from './menuItem';
import { buttonGroupTheme } from './buttonGroup';
import { tooltipTheme } from './tooltip';
import { DEFAULT_BUTTON_INPUT_ELEVATION, SPACING_SIZES, FONT, ICON } from './commons';
import { fabTheme } from './fab';
import { Palette } from '@mui/material/styles/createPalette';
import { cardTheme } from './card';
import { radioTheme } from './radio';
import { paginationTheme } from './pagination';
import { listTheme } from './list';
import { tabsTheme } from './tabs';
import { switchTheme } from './switch';
import { paperTheme } from './paper';
import { dateCalendar } from './dateCalendar';
import { dialogTheme } from './dialog';
import { selectTheme } from './select';
import { typographyTheme } from './typography';
import { stepperTheme } from './stepper';
import { chipTheme } from './chip';
import { accordionTheme } from './accordion';
import { tokens } from './colors';

const spacings = [0, 2, 4, 8, 12, 16, 24, 32];
export const baseTheme = {
  defaultFontSize: '16px',
  font: FONT,
  icon: ICON,
  boxShadow: DEFAULT_BUTTON_INPUT_ELEVATION.boxShadow,
  spacingSize: SPACING_SIZES,
  borderRadius: SPACING_SIZES,
  tokens,
  // Mui Props
  palette: {
    default: {
      main: tokens.source.colorOnSurface,
      light: tokens.source.colorSurfaceVariant,
      dark: tokens.source.colorOnSurface,
      contrastText: tokens.source.colorSurfaceVariant
    } as Palette['primary'],
    action: {
      checked: tokens.source.colorPrimary,
      active: tokens.source.colorPrimary,
      hover: tokens.source.statePrimaryHoverStateLayerColor,
      hoverOpacity: tokens.source.stateOpacityHoverStateLayerOpacity as unknown as number,
      selected: tokens.source.colorPrimary,
      selectedOpacity: tokens.stateOpacityHoverStateLayerOpacity as unknown as number,
      disabled: tokens.source.stateOpacityDisabledContentStateOpacity,
      disabledOpacity: tokens.stateOpacityDisabledContentStateOpacity as unknown as number,
      disabledBackground: tokens.source.stateOnSurfaceDisabledContentStateLayerColor,
      focus: tokens.source.statePrimaryFocusStateLayerColor,
      focusOpacity: tokens.source.stateOpacityFocusStateLayerOpacity as unknown as number,
      activatedOpacity: tokens.source.stateOpacityHoverStateLayerOpacity as unknown as number
    } as Palette['action'],
    secondary: { main: tokens.source.colorSecondary },
    primary: { main: tokens.source.colorPrimary },
    error: { main: tokens.source.colorAlert },
    background: {
      default: tokens.source.colorSurface
    }
  } as Palette & { default: Palette['primary'] },
  components: {
    ...labelTheme,
    ...inputTheme,
    ...buttonTheme,
    ...buttonGroupTheme,
    ...dividerTheme,
    ...menuItemTheme,
    ...tooltipTheme,
    ...fabTheme,
    ...cardTheme,
    ...radioTheme,
    ...paginationTheme,
    ...listTheme,
    ...tabsTheme,
    ...switchTheme,
    ...paperTheme,
    ...dateCalendar,
    ...dialogTheme,
    ...selectTheme,
    ...typographyTheme,
    ...stepperTheme,
    ...chipTheme,
    ...accordionTheme
  } satisfies Components,

  // we use our own tokens
  cssVariables: false,
  typography: {
    fontFamily: "'Roboto', 'Helvetica Neue', 'Helvetica', Arial, sans-serif",
    fontWeight: 400,
    fontSize: 14
  },
  spacing: (factor: number | 'auto' = 0) => {
    if (factor === 'auto') {
      return 'auto';
    }
    const sign = factor >= 0 ? 1 : -1;
    const factorAbs = Math.min(Math.abs(factor), spacings.length - 1);
    if (Number.isInteger(factor)) {
      return (spacings[factorAbs] ?? 0) * sign;
    }
    return interpolate(factorAbs, spacings) * sign;
  }
} as const;

export type BaseTheme = typeof baseTheme;

const interpolate = (value: number, array: readonly number[]) => {
  const floor = Math.floor(value);
  const ceil = Math.ceil(value);
  const diff = value - floor;
  return (array[floor] ?? 0) + ((array[ceil] ?? 0) - (array[floor] ?? 0)) * diff;
};
